import Input from "component/design-system/form/Input";
import PAImage from "component/design-system/images/PAImage";
import LoaderSkeleton from "component/loader-skeleton/LoaderSkeleton";
import Alert from "component/react-bootstrap/Alert";
import PACross from "component/react-bootstrap/pa-icons/PACross";
import { useTranslation } from "next-i18next";
import { useEffect, useRef, useState } from "react";
import HideIf from "util/HideIf";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import styles from "./fileUpload.module.scss";

interface FileUploadImage {
  fileData: File;
  name: string;
  url: string;
}

interface IProps {
  setImages: (files: File[]) => void
  index?: number,
  images?: FileUploadImage[],
  uploadHelper?: boolean,
  formRef?: any,
  removeFile: (files: any, index: number) => void
  onlyPreview?: boolean;
  uploadSingleImage?: boolean;
  multiple?: boolean;
  fileSize?: number;
}

function FileUpload(props: IProps) {
  const {
    setImages, index = -1, images = [],
    uploadHelper = true, formRef, removeFile,
    onlyPreview = false, uploadSingleImage = false, multiple = false,
    fileSize,
  } = props;

  const { t } = useTranslation("account");

  const [imagePlaceHolder, setImagePlaceholder] = useState([]);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [fileSizeError, setFileSizeError] = useState(false);
  const attachImageRef = useRef();

  const imagesToRender = images && images.length > 0 ? images : imagePlaceHolder;
  const inputName = `fileUpload${index >= 0 ? `-${index}` : ""}`;

  useEffect(() => {
    if (images && images.length > 0) {
      setImagePlaceholder([]);
    }
  }, [images]);

  function previewFile(attachFileEvent: any) {
    const { files } = attachFileEvent.target; // FileList object

    Object.keys(files).forEach((key: number) => {
      const file = files[key];
      // Only pics
      if (file.type.match("image") && file.size < (fileSize || 300000)) {
        const picReader = new FileReader();
        picReader.addEventListener("load", (event) => {
          const picFile = event.target;

          if (multiple) {
            setPreviewImages((prevPreviewImages: string[]) => [
              ...prevPreviewImages,
              picFile.result,
            ]);
          } else {
            // feedback modal only have single image
            setPreviewImages(
              [picFile.result],
            );
          }
        });
        // Read the image
        picReader.readAsDataURL(file);
      }
    });
  }

  function attachImage(event: any) {
    event.preventDefault();
    setFileSizeError(false);
    if (onlyPreview) {
      previewFile(event);
    }

    const { files } = event.target;
    const updatedFiles: File[] = [];

    if (files && Object.keys(files).length > 0) {
      Object.keys(files).forEach((key: number) => {
        const singleFile = files[key];
        if (singleFile.size > (fileSize || 300000)) {
          if (formRef && formRef.current) {
            formRef.current[inputName].setAttribute("error", "");
          }

          setFileSizeError(true);
        } else {
          singleFile.preview = window.URL.createObjectURL(singleFile);
          updatedFiles.push(singleFile);
        }
      });

      setImagePlaceholder(updatedFiles);
      if (multiple) {
        setImages([...images, ...updatedFiles]);
      } else {
        setImages(updatedFiles);
      }
    }
  }

  function removeImage(event: any, imageIndex) {
    if (event) event.preventDefault();
    const updatedFiles = [...images];
    updatedFiles.splice(imageIndex, 1);
    removeFile(updatedFiles, index);
  }

  function removePreviewImage(imageIndex: number) {
    const updatedPreviewImages = [...previewImages];
    const updatedFiles = [...images];
    updatedFiles.splice(imageIndex, 1);
    updatedPreviewImages.splice(imageIndex, 1);
    setPreviewImages(updatedPreviewImages);
    setImages(updatedFiles);
  }

  return (
    <>
      <HideIf show={uploadHelper}>
        <span style={{ paddingTop: "calc(2 * var(--safari-root-font, 10px))" }}>
          {`${t("product_photo_upload")} `}
        </span>
      </HideIf>

      <label
        htmlFor={inputName}
        className={`btn btn-default image-upload-button ${styles.file_upload_label}`}
      >

        <Input
          name={inputName}
          id={inputName}
          type="file"
          accept="image/*"
          onChange={(e) => attachImage(e)}
          ref={attachImageRef}
          multiple={!uploadSingleImage}
        />
        {/* <div id="result" className={styles.preview_image_container} /> */}

        <span>{`${t("Attach Image")}${!uploadSingleImage ? "(s)" : ""}` }</span>

      </label>
      <div
        style={{
          display: "flex", fontSize: "calc(1.5 * var(--safari-root-font, 10px))", flexWrap: "wrap", justifyContent: "center",
        }}
        className={styles.file_container}
      >
        <HideIf show={imagesToRender && imagesToRender.length > 0}>
          <div className={styles.preview_image_container}>

            {
              imagesToRender.map((file, imageIndex: number) => (
                <div key={file.url || file.name} className={styles.file_preview}>
                  <div className={styles.preview_image}>
                    <HideIf show={file.url && !onlyPreview}>
                      <PAImage src={getRelativeImagePath(file.url)} width={95} height={95} />
                      <PACross className={styles.remove_image} onClick={(e) => removeImage(e, imageIndex)} />
                    </HideIf>
                    <HideIf show={!file.url && !onlyPreview}>
                      <LoaderSkeleton width={95} height={95} />
                    </HideIf>
                    <HideIf show={onlyPreview && previewImages[imageIndex]}>
                      <>
                        <PAImage src={previewImages[imageIndex]} width={95} height={95} />
                        <PACross className={styles.remove_image} onClick={() => removePreviewImage(imageIndex)} />
                      </>
                    </HideIf>

                  </div>
                  <span style={{ color: "#bdbdbd" }}>{`${file.name}`}</span>

                </div>
              ))
            }

          </div>
        </HideIf>
        {/*
        <PAText
          className={styles.remove_button}
          onClick={(e) => removeImage(e)}
        >
          {t("Remove")}
        </PAText>

        <PAText
          className={styles.change_button}
        >
          {t("Change")}
        </PAText> */}
      </div>
      {fileSizeError && (
        <Alert bsStyle="danger">
          {t("The uploaded image is too large. Maximum size is 290 KB.")}
        </Alert>
      )}
    </>
  );
}

export default FileUpload;
